import { Button, VStack } from '@chakra-ui/react';
import { useForm, useWatch } from 'react-hook-form';
import { Modal } from '@/client/components/shared/Modal';
import { FormField } from '@/components/Modal/FormField';
import { useSendTestSummary, useSendTestReminder, useSimulateCommand } from '@/lib/sdk/delivery/queries';
import { useChatbots } from '@/lib/sdk/chatbot/queries';
import { useToast } from '@/client/hooks/useToast';

interface TestNotificationFormData {
  mode: 'template' | 'simulate';
  template: string;
  chatbot_id: string;
  phone_number: string;
  mobile: string;
  test_mobile: string;
  text: string;
  sender_name: string;
}

interface TestNotificationModalProps {
  orgId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function TestNotificationModal({ orgId, isOpen, onClose }: TestNotificationModalProps) {
  const toast = useToast();
  const { register, handleSubmit, reset, control } = useForm<TestNotificationFormData>({
    defaultValues: {
      mode: 'template',
      template: 'summary',
      chatbot_id: '',
      phone_number: '',
      mobile: '',
      test_mobile: '',
      text: '',
      sender_name: '',
    },
  });
  const mode = useWatch({ control, name: 'mode' });

  const { mutate: sendTestSummary, isPending: isSendingSummary } = useSendTestSummary(orgId);
  const { mutate: sendTestReminder, isPending: isSendingReminder } = useSendTestReminder(orgId);
  const { mutate: simulateCommand, isPending: isSimulating } = useSimulateCommand(orgId);
  const isSending = isSendingSummary || isSendingReminder || isSimulating;

  const { data: chatbotsData } = useChatbots({ orgId, active_only: true });
  const chatbots = chatbotsData?.items ?? [];

  const onSubmit = (data: TestNotificationFormData) => {
    if (data.mode === 'simulate') {
      const { mobile, test_mobile, text, sender_name } = data;
      simulateCommand(
        { mobile, test_mobile, text, ...(sender_name ? { sender_name } : {}) },
        {
          onSuccess: (res) => {
            if (res.processed) {
              toast.success('Command processed, reply sent to test number');
              reset();
              onClose();
            } else {
              toast.error(res.error || 'No matching command or action found');
            }
          },
          onError: () => toast.error('Failed to simulate command'),
        }
      );
      return;
    }

    const { template, phone_number, chatbot_id } = data;
    const isReminder = template === 'reminder';
    const label = isReminder ? 'Reminder' : 'Summary';
    const mutate = isReminder ? sendTestReminder : sendTestSummary;
    const payload = { phone_number, ...(chatbot_id ? { chatbot_id } : {}) };
    mutate(payload, {
      onSuccess: (res) => {
        if (res.sent) {
          toast.success(`${label} sent successfully`);
          reset();
          onClose();
        } else {
          toast.error('No data available for this org');
        }
      },
      onError: () => toast.error(`Failed to send ${label.toLowerCase()}`),
    });
  };

  const footer = (
    <Button
      colorScheme="brand"
      isLoading={isSending}
      loadingText={mode === 'simulate' ? 'Simulating...' : 'Sending...'}
      onClick={handleSubmit(onSubmit)}
    >
      {mode === 'simulate' ? 'Run Simulation' : 'Send Test'}
    </Button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Test Notification" size="md" footer={footer}>
      <VStack spacing={4} as="form" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          label="Mode"
          name="mode"
          register={register}
          type="select"
          options={[
            { value: 'template', label: 'Template Message' },
            { value: 'simulate', label: 'Simulate Command/Action' },
          ]}
          required
        />
        {mode === 'simulate' ? (
          <>
            <FormField
              label="User's Mobile Number"
              name="mobile"
              register={register}
              placeholder="+91XXXXXXXXXX"
              required
              description="The real user whose command/action is being simulated. All database state is attributed to this number."
            />
            <FormField
              label="Tester's Mobile Number"
              name="test_mobile"
              register={register}
              placeholder="+91XXXXXXXXXX"
              required
              description="The reply is delivered here instead of the user's number."
            />
            <FormField
              label="Command / Action"
              name="text"
              register={register}
              placeholder="/start ABC123"
              required
              description="Raw command (e.g. /start ABC123) or button/list action id, exactly as it would arrive from WhatsApp."
            />
            <FormField
              label="Sender Name"
              name="sender_name"
              register={register}
              placeholder="Optional"
              description="Used only if the user's mobile number has no matching team member."
            />
          </>
        ) : (
          <>
            <FormField
              label="Template"
              name="template"
              register={register}
              type="select"
              options={[
                { value: 'summary', label: 'Summary' },
                { value: 'reminder', label: 'Reminder' },
              ]}
              required
            />
            <FormField
              label="Chatbot"
              name="chatbot_id"
              register={register}
              type="select"
              options={[
                { value: '', label: 'Org (all lists)' },
                ...chatbots.map((c) => ({ value: c.id, label: c.title })),
              ]}
            />
            <FormField
              label="Phone Number"
              name="phone_number"
              register={register}
              placeholder="+91XXXXXXXXXX"
              required
              description="Send the WhatsApp notification to this number"
            />
          </>
        )}
      </VStack>
    </Modal>
  );
}
